import { TreeDataNode, TreeOptions } from "@gtmhub/shared/tree-renderer/tree-models";
import { TreeRenderer } from "@gtmhub/shared/tree-renderer/tree-renderer";
import { ITeam } from "@gtmhub/teams";
import { CurvedTreeLink } from "@gtmhub/teams/utils/curved-tree-link";
import TreeNode, { OrganizationTreeNodeOptions } from "./organization-tree-node";
import OrganizationTreeNode from "./organization-tree-node";

type OrganizationTreeOptions = TreeOptions &
  OrganizationTreeNodeOptions & {
    isNodeDraggingEnabled?: boolean;
  };

export class OrganizationTreeRenderer extends TreeRenderer {
  protected treeNodeClass = TreeNode;
  protected treeLinkClass = CurvedTreeLink;

  private isNodeDraggingEnabled: boolean;
  private teamMembersCollapsedMap: { [key: string]: boolean };

  private nodeOptions: OrganizationTreeNodeOptions;

  constructor(protected treeContainer: HTMLElement) {
    super(treeContainer);

    this.nodeOptions = {};
    this.setOptions({ showRoot: false, isNodeDraggingEnabled: false });

    this.teamMembersCollapsedMap = {};

    this.forbiddenMousePanTargets = [
      "drag-handle",
      "drag-container",
      "nc-icon-outline",
      "team-name",
      "manager-name",
      "member-name",
      "children-count",
      "fold-children",
      "info-label",
    ];
  }

  /**
   * generateLocalStorageKey
   * Generates a unique localStorage key for the tree snapshot.
   *
   * @return {string}
   */
  private generateLocalStorageKey() {
    return `org-tree-${JSON.parse(localStorage.getItem("userId"))}-${JSON.parse(localStorage.getItem("accountId"))}`;
  }

  /**
   * getBranchStates
   * Gets the folded tree branches in Array of {nodeId: true/false} format.
   *
   * @param {TreeDataNode} rootNode
   * @return { [key: string]: boolean }
   */
  private getBranchStates(rootNode: TreeDataNode): { [nodeId: string]: boolean } {
    const foldedState = {};

    this.process(rootNode, (node) => {
      if (node.folded) {
        foldedState[node.id] = true;
      }
    });

    return foldedState;
  }

  /**
   * applyBranchState
   * Applies the fold state of a given tree branch.
   *
   * @param {TreeDataNode} node
   * @param { [nodeId: string]: boolean }
   * @return {void}
   */
  private applyBranchState(node, states: { [key: string]: boolean }): void {
    if (states[node.id]) {
      node.folded = true;
    }
  }

  /**
   * createNode
   * Uses the super class to create a node normally and add it to "this.treeNodes", then applies any node options,
   * that were set using "this.setOptions()".
   *
   * @param {TreeDataNode} node
   * @return {void}
   */
  protected createNode(node: TreeDataNode): void {
    super.createNode(node);
    const isMemberListCollapsed = node.id in this.teamMembersCollapsedMap && this.teamMembersCollapsedMap[node.id] === true;
    (this.treeNodes.get(node.id) as OrganizationTreeNode).setOptions({ ...this.nodeOptions, isMemberListCollapsed });
  }

  /**
   * setOptions
   * Sets the Organization Tree configuration, ignoring the currently unaccepatable "showRoot" option.
   *
   * @param {OrganizationTreeOptions} options
   * @return {this}
   */
  public setOptions(options: OrganizationTreeOptions): this {
    // node options
    // preserve these for nodes create later
    this.nodeOptions.hasManagePeoplePermissions =
      options.hasManagePeoplePermissions !== undefined ? options.hasManagePeoplePermissions : this.nodeOptions.hasManagePeoplePermissions;
    this.nodeOptions.maxTooltipLength = options.maxTooltipLength !== undefined ? options.maxTooltipLength : this.nodeOptions.maxTooltipLength;
    this.nodeOptions.showSomeMembers = options.showSomeMembers !== undefined ? options.showSomeMembers : this.nodeOptions.showSomeMembers;
    this.nodeOptions.someMembersLimit = options.someMembersLimit !== undefined ? options.someMembersLimit : this.nodeOptions.someMembersLimit;

    this.treeNodes.forEach((node: OrganizationTreeNode) => {
      node.setOptions(this.nodeOptions);
    });

    // Organization Tree-only options
    this.isNodeDraggingEnabled = options.isNodeDraggingEnabled !== undefined ? options.isNodeDraggingEnabled : this.isNodeDraggingEnabled;

    // currently, the Organization Tree is not meant to have a "root" (the company name, or the supreme leader, or something else),
    // so we explicitly disable it, not to break the design.
    return super.setOptions({
      ...options,
      showRoot: false,
    });
  }

  public getOptions(): OrganizationTreeOptions {
    return {
      ...super.getOptions(),
      ...this.nodeOptions,
      isNodeDraggingEnabled: this.isNodeDraggingEnabled,
    };
  }

  /**
   * isThereState
   * Checks whether the tree state has ever been saved in the localStorage and if the snapshot is valid.
   *
   * @return {boolean}
   */
  public isThereState(): boolean {
    const stateString = localStorage.getItem(this.generateLocalStorageKey());
    if (!stateString) {
      return false;
    }

    try {
      JSON.parse(stateString);
    } catch (e) {
      return false;
    }

    return true;
  }

  /**
   * saveState
   * Saves the tree state into localStorage.
   *
   * @param {void}
   * @return {this}
   */
  public saveState(): this {
    try {
      const settings = JSON.stringify({
        branchStates: this.getBranchStates(this.inputNodes),
        isNodeDraggingEnabled: !!this.isNodeDraggingEnabled,
      });
      localStorage.setItem(this.generateLocalStorageKey(), settings);
    } catch (e) {
      const err = new Error(`Failed saving Organization Tree state. ${e.message}`);
      err.stack = e.stack;
      throw err;
    }

    return this;
  }

  /**
   * loadState
   * Loads the tree state from localStorage.
   *
   * @param {void}
   * @return {this}
   */
  public loadState(): this {
    if (!this.isThereState()) {
      return this;
    }

    const stateString = localStorage.getItem(this.generateLocalStorageKey());

    try {
      const state = JSON.parse(stateString);
      if (state.branchStates) {
        this.process(this.inputNodes, (node) => this.applyBranchState(node, state.branchStates));
        this.applyBranchState(this.inputNodes, state.branchStates);
      }

      this.isNodeDraggingEnabled = !!state?.isNodeDraggingEnabled;
    } catch (e) {
      const err = new Error(`Failed restoring Organization Tree state. ${e.message}`);
      err.stack = e.stack;
      throw err;
    }

    return this;
  }

  /**
   * Returns flattened structure of all sub-teams.
   * @param teamId
   * @return {ITeam[]}
   */
  public getAllChildTeams(teamId: string): ITeam[] {
    const descendants = this.findInputNode(teamId, this.inputNodes).descendants() as TreeDataNode<ITeam>[];
    return descendants.map((d) => d.data);
  }

  /**
   * collapseTeamMembers
   *
   * @param {string} teamId
   * @return {this}
   */
  public collapseTeamMembers(teamId: string) {
    this.teamMembersCollapsedMap[teamId] = true;

    if (this.treeNodes.has(teamId)) {
      (this.treeNodes.get(teamId) as OrganizationTreeNode).collapseMembers();
    }

    return this;
  }

  /**
   * expandTeamMembers
   *
   * @param {string} teamId
   * @return {this}
   */
  public expandTeamMembers(teamId: string) {
    this.teamMembersCollapsedMap[teamId] = false;

    if (this.treeNodes.has(teamId)) {
      (this.treeNodes.get(teamId) as OrganizationTreeNode).expandMembers();
    }

    return this;
  }
}
